/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `AuthSessionContext` for the Caintra Connect frontend.
 * @module context/AuthSessionContext
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { AppContext } from "./AppContext";
import { getToken } from "../utils/common.js";
import { getUser, getUserRoleData } from "../logic/user";

export const AUTH_SESSION_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  UNAUTHENTICATED: "unauthenticated",
};

const AuthSessionContext = createContext(null);

/**
 * Central auth session store: user profile, permissions, and refresh lifecycle.
 * Syncs permissions into AppContext for existing consumers.
 */
export const AuthSessionProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);

  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [status, setStatus] = useState(AUTH_SESSION_STATUS.IDLE);

  const lastTipoRef = useRef(undefined);
  const permissionsRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const statusRef = useRef(status);

  permissionsRef.current = permissions;
  statusRef.current = status;

  const clearSession = useCallback(() => {
    setUser(null);
    setPermissions(null);
    permissionsRef.current = null;
    lastTipoRef.current = undefined;
    changePermissions(null);
    setStatus(AUTH_SESSION_STATUS.UNAUTHENTICATED);
  }, [changePermissions]);

  const applySession = useCallback(
    (userData, perms) => {
      setUser(userData);
      setPermissions(perms);
      permissionsRef.current = perms;
      if (perms != null) {
        changePermissions(perms);
      }
      if (userData?.tipoDeUsuario != null) {
        lastTipoRef.current = userData.tipoDeUsuario;
      }
      setStatus(AUTH_SESSION_STATUS.READY);
    },
    [changePermissions],
  );

  /**
   * Loads or refreshes the session from the API.
   * @param {{ background?: boolean }} [options]
   *   When true, keeps the current UI visible and skips the loading status.
   */
  const refreshSession = useCallback(
    async (options = {}) => {
      const { background = false } = options;

      if (!getToken()) {
        clearSession();
        return null;
      }

      const userId = localStorage.getItem("userId");
      if (!userId) {
        clearSession();
        return null;
      }

      if (!background && statusRef.current !== AUTH_SESSION_STATUS.READY) {
        setStatus(AUTH_SESSION_STATUS.LOADING);
      }

      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      const promise = (async () => {
        try {
          const userData = await getUser(userId, navigate);
          if (!userData) {
            if (!background) {
              clearSession();
            }
            return null;
          }

          const previousTipo = lastTipoRef.current;
          const nextTipo = userData.tipoDeUsuario;
          const tipoChanged =
            previousTipo !== undefined && previousTipo !== nextTipo;
          const shouldFetchPermissions =
            permissionsRef.current == null || tipoChanged;

          let perms = permissionsRef.current;
          if (shouldFetchPermissions) {
            const fetchedPerms = await getUserRoleData(userId, navigate);
            if (fetchedPerms != null) {
              perms = fetchedPerms;
            }
          }

          applySession(userData, perms);
          return userData;
        } catch (error) {
          logger.error("Failed to refresh auth session:", {
            err: sanitizeError(error),
          });
          if (!background) {
            clearSession();
          }
          return null;
        } finally {
          refreshInFlightRef.current = null;
        }
      })();

      refreshInFlightRef.current = promise;
      return promise;
    },
    [applySession, clearSession, navigate],
  );

  /**
   * Called after login/SSO once tokens are persisted in localStorage.
   * @param {{ permissions?: unknown }} [options]
   */
  const establishSession = useCallback(
    async (options = {}) => {
      const userId = localStorage.getItem("userId");
      if (!userId || !getToken()) {
        clearSession();
        return null;
      }

      setStatus(AUTH_SESSION_STATUS.LOADING);

      const userData = await getUser(userId, navigate);
      if (!userData) {
        clearSession();
        return null;
      }

      let perms = options.permissions ?? null;
      if (perms == null) {
        perms = await getUserRoleData(userId, navigate);
      }

      applySession(userData, perms);
      return userData;
    },
    [applySession, clearSession, navigate],
  );

  // Bootstrap session once on app load.
  useEffect(() => {
    if (statusRef.current !== AUTH_SESSION_STATUS.IDLE) {
      return;
    }

    if (getToken()) {
      refreshSession({ background: false });
      return;
    }

    setStatus(AUTH_SESSION_STATUS.UNAUTHENTICATED);
  }, [refreshSession]);

  // Revalidate in the background when navigating between app routes.
  useEffect(() => {
    if (statusRef.current !== AUTH_SESSION_STATUS.READY) {
      return;
    }

    refreshSession({ background: true });
  }, [location.pathname, refreshSession]);

  // Revalidate when the tab becomes visible or the window regains focus.
  useEffect(() => {
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        statusRef.current === AUTH_SESSION_STATUS.READY
      ) {
        refreshSession({ background: true });
      }
    };

    const onFocus = () => {
      if (statusRef.current === AUTH_SESSION_STATUS.READY) {
        refreshSession({ background: true });
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshSession]);

  const value = {
    user,
    permissions,
    status,
    refreshSession,
    establishSession,
    clearSession,
  };

  return <AuthSessionContext value={value}>{children}</AuthSessionContext>;
};

/**
 * @returns {{
 *   user: object|null,
 *   permissions: unknown,
 *   status: string,
 *   refreshSession: (options?: { background?: boolean }) => Promise<object|null>,
 *   establishSession: (options?: { permissions?: unknown }) => Promise<object|null>,
 *   clearSession: () => void,
 * }}
 */
export const useAuthSession = () => {
  const context = use(AuthSessionContext);
  if (!context) {
    throw new Error("useAuthSession must be used within AuthSessionProvider");
  }
  return context;
};

export default AuthSessionContext;
